/**
 * AddCompanyModal Component
 * 
 * Modal for adding new companies to the system.
 * Integrates with the backend company creation endpoint.
 * 
 * @component
 * @example
 * <AddCompanyModal 
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   onSuccess={handleCompanyAdded}
 * />
 */

import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';
import './AddCompanyModal.scss';

interface AddCompanyModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Success callback after company is added */
  onSuccess?: (data?: any) => void;
}

interface CompanyFormData {
  name: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  postal_code: string;
  website?: string;
  description?: string;
  plan?: string;  // Plan: basic, plus, pro
  logo_file?: string;  // Base64 encoded logo
  logo_filename?: string;  // Original filename
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    postal_code: '',
    website: '',
    description: '',
    plan: 'basic',  // Default plan
    logo_file: undefined,
    logo_filename: undefined
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<CompanyFormData>>({});
  const [showExcelSection, setShowExcelSection] = useState(false);
  const [excelData, setExcelData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<any>({});
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Handle input changes
  const handleInputChange = (field: keyof CompanyFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };


  // Download Excel template
  const downloadTemplate = () => {
    const templateData = [
      {
        name: 'Company Name',
        email: 'company@example.com',
        phone: '+1234567890',
        country: 'Turkey',
        city: 'Istanbul',
        postal_code: '34000',
        website: 'https://www.company.com',
        description: 'Company description here'
      }
    ];

    const csvContent = [
      'name,email,phone,country,city,postal_code,website,description',
      ...templateData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'company_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Excel file upload
  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',');
      
      const data = lines.slice(1)
        .filter(line => line.trim())
        .map(line => {
          const values = line.split(',');
          const row: any = {};
          headers.forEach((header, index) => {
            row[header.trim()] = values[index]?.trim() || '';
          });
          return row;
        });

      setExcelData(data);
      setShowPreview(true);
    };
    reader.readAsText(file);
  };

  // Handle edit row in preview
  const handleEditRow = (index: number) => {
    setEditingRow(index);
    setEditedData({ ...excelData[index] });
  };

  // Handle save edited row
  const handleSaveRow = (index: number) => {
    setExcelData(prev => prev.map((row, i) => 
      i === index ? { ...prev[i], ...editedData } : row
    ));
    setEditingRow(null);
    setEditedData({});
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedData({});
  };

  // Handle delete row
  const handleDeleteRow = (index: number) => {
    setExcelData(prev => prev.filter((_, i) => i !== index));
  };

  // Handle select/deselect row
  const handleSelectRow = (index: number) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Handle select all rows
  const handleSelectAll = () => {
    if (selectedRows.size === excelData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(excelData.map((_, index) => index)));
    }
  };

  // Handle delete selected rows
  const handleDeleteSelected = () => {
    setExcelData(prev => prev.filter((_, index) => !selectedRows.has(index)));
    setSelectedRows(new Set());
  };

  // Handle edited data change
  const handleEditedDataChange = (field: string, value: string) => {
    setEditedData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle logo file upload
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        logo_file: 'Please select an image file'
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        logo_file: 'File size must be less than 5MB'
      }));
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setFormData(prev => ({
        ...prev,
        logo_file: result,
        logo_filename: file.name
      }));
      
      // Clear error
      if (errors.logo_file) {
        setErrors(prev => ({
          ...prev,
          logo_file: undefined
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Partial<CompanyFormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.postal_code.trim()) {
      newErrors.postal_code = 'Postal code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle bulk company creation
  const handleBulkCreate = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Create companies one by one
      const results = [];
      for (const companyData of excelData) {
        const response = await fetch(API_ENDPOINTS.COMPANIES, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(companyData)
        });

        if (!response.ok) {
          throw new Error(`Failed to create company: ${companyData.name}`);
        }

        const result = await response.json();
        results.push(result);
      }

      console.log('Bulk creation successful:', results);
      setExcelData([]);
      setShowPreview(false);
      setShowExcelSection(false);
      
      if (onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      console.error('Bulk creation error:', error);
      alert(`Error creating companies: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(API_ENDPOINTS.COMPANIES, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create company');
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        console.log('Company created successfully:', result);
        
        // Show generated codes if available (V2 feature)
        if (result.codes) {
          console.log('Generated codes:', result.codes);
          // You can show these codes to the user if needed
          alert(`Company created successfully!\n\nGenerated Codes:\nUser Code: ${result.codes.user_code}\nAdmin Code: ${result.codes.admin_code}\nProcurement Code: ${result.codes.procurement_code}`);
        }
        
        // Reset form and close modal
        setFormData({
          name: '',
          email: '',
          phone: '',
          country: '',
          city: '',
          postal_code: '',
          website: '',
          description: '',
          plan: 'basic',
          logo_file: undefined,
          logo_filename: undefined
        });
        setErrors({});
        
        onSuccess?.(result.data);
        onClose();
      } else {
        throw new Error(result.message || 'Failed to create company');
      }
      
    } catch (error) {
      console.error('Error creating company:', error);
      alert(error instanceof Error ? error.message : 'Failed to create company');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        postal_code: '',
        website: '',
        description: '',
        plan: 'basic',
        logo_file: undefined,
        logo_filename: undefined
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-company-modal-overlay-v2">
      <div className="add-company-modal-v2">
        <div className="modal-header-v2">
          <h2 className="modal-title-v2">Add New Customer</h2>
          <button 
            className="modal-close-button-v2"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        
        <div className="modal-tabs-v2">
          <button 
            type="button"
            className={`tab-button-v2 ${!showExcelSection ? 'active-v2' : ''}`}
            onClick={() => setShowExcelSection(false)}
          >
            Single Entry
          </button>
          <button 
            type="button"
            className={`tab-button-v2 ${showExcelSection ? 'active-v2' : ''}`}
            onClick={() => setShowExcelSection(true)}
          >
            Excel Import
          </button>
        </div>

        {!showExcelSection ? (
          <form onSubmit={handleSubmit} className="modal-form-v2">
          <div className="form-group-v2">
            <label htmlFor="name" className="form-label-v2">
              Company Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`form-input-v2 ${errors.name ? 'error-v2' : ''}`}
              placeholder="Enter company name"
              disabled={isSubmitting}
            />
            {errors.name && <span className="error-message-v2">{errors.name}</span>}
          </div>
          
          <div className="form-group-v2">
            <label htmlFor="email" className="form-label-v2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`form-input-v2 ${errors.email ? 'error-v2' : ''}`}
              placeholder="Enter email address"
              disabled={isSubmitting}
            />
            {errors.email && <span className="error-message-v2">{errors.email}</span>}
          </div>

          <div className="form-group-v2">
            <label htmlFor="logo" className="form-label-v2">
              Company Logo
            </label>
            <input
              type="file"
              id="logo"
              accept="image/*"
              onChange={handleLogoUpload}
              className="form-input-v2"
              disabled={isSubmitting}
            />
            {formData.logo_filename && (
              <div className="logo-preview-v2">
                <span>Selected: {formData.logo_filename}</span>
                <img 
                  src={formData.logo_file} 
                  alt="Logo preview" 
                  style={{ maxWidth: '100px', maxHeight: '100px', marginTop: '8px' }}
                />
              </div>
            )}
            {errors.logo_file && <span className="error-message-v2">{errors.logo_file}</span>}
          </div>
          
          <div className="form-group-v2">
            <label htmlFor="phone" className="form-label-v2">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="form-input-v2"
              placeholder="Enter phone number"
              disabled={isSubmitting}
            />
          </div>
          
          <div className="form-group-v2">
            <label htmlFor="country" className="form-label-v2">
              Country *
            </label>
            <input
              type="text"
              id="country"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className={`form-input-v2 ${errors.country ? 'error-v2' : ''}`}
              placeholder="Enter country"
              disabled={isSubmitting}
            />
            {errors.country && <span className="error-message-v2">{errors.country}</span>}
          </div>
          
          <div className="form-group-v2">
            <label htmlFor="city" className="form-label-v2">
              City *
            </label>
            <input
              type="text"
              id="city"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className={`form-input-v2 ${errors.city ? 'error-v2' : ''}`}
              placeholder="Enter city"
              disabled={isSubmitting}
            />
            {errors.city && <span className="error-message-v2">{errors.city}</span>}
          </div>
          
          <div className="form-group-v2">
            <label htmlFor="postal_code" className="form-label-v2">
              Postal Code *
            </label>
            <input
              type="text"
              id="postal_code"
              value={formData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              className={`form-input-v2 ${errors.postal_code ? 'error-v2' : ''}`}
              placeholder="Enter postal code"
              disabled={isSubmitting}
            />
            {errors.postal_code && <span className="error-message-v2">{errors.postal_code}</span>}
          </div>

          <div className="form-group-v2">
            <label htmlFor="website" className="form-label-v2">
              Website
            </label>
            <input
              type="url"
              id="website"
              value={formData.website || ''}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="form-input-v2"
              placeholder="https://www.company.com"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group-v2">
            <label htmlFor="plan" className="form-label-v2">
              Plan
            </label>
            <select
              id="plan"
              value={formData.plan || 'basic'}
              onChange={(e) => handleInputChange('plan', e.target.value)}
              className="form-input-v2"
              disabled={isSubmitting}
            >
              <option value="basic">Basic</option>
              <option value="plus">Plus</option>
              <option value="pro">Pro</option>
            </select>
          </div>

          <div className="form-group-v2">
            <label htmlFor="description" className="form-label-v2">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="form-input-v2"
              placeholder="Enter company description"
              rows={3}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="modal-actions-v2">
            <button
              type="button"
              className="cancel-button-v2"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button-v2"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Company'}
            </button>
          </div>
        </form>
        ) : (
          <div className="excel-section-v2">
            <div className="excel-actions-v2">
              <button
                type="button"
                className="template-button-v2"
                onClick={downloadTemplate}
              >
                Download Template
              </button>
              <input
                type="file"
                accept=".csv"
                onChange={handleExcelUpload}
                className="file-input-v2"
                id="excel-upload"
              />
              <label htmlFor="excel-upload" className="upload-button-v2">
                Upload Excel File
              </label>
            </div>

            {showPreview && (
              <div className="preview-section-v2">
                <h3>Preview ({excelData.length} companies)</h3>
                <div className="preview-table-v2">
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectedRows.size === excelData.length && excelData.length > 0}
                            onChange={handleSelectAll}
                            className="select-all-checkbox-v2"
                          />
                        </th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Country</th>
                        <th>City</th>
                        <th>Postal Code</th>
                        <th>Website</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.map((row, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedRows.has(index)}
                              onChange={() => handleSelectRow(index)}
                              className="row-checkbox-v2"
                            />
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="text"
                                value={editedData.name || ''}
                                onChange={(e) => handleEditedDataChange('name', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.name
                            )}
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="email"
                                value={editedData.email || ''}
                                onChange={(e) => handleEditedDataChange('email', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.email
                            )}
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="text"
                                value={editedData.phone || ''}
                                onChange={(e) => handleEditedDataChange('phone', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.phone
                            )}
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="text"
                                value={editedData.country || ''}
                                onChange={(e) => handleEditedDataChange('country', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.country
                            )}
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="text"
                                value={editedData.city || ''}
                                onChange={(e) => handleEditedDataChange('city', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.city
                            )}
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="text"
                                value={editedData.postal_code || ''}
                                onChange={(e) => handleEditedDataChange('postal_code', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.postal_code
                            )}
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="url"
                                value={editedData.website || ''}
                                onChange={(e) => handleEditedDataChange('website', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.website
                            )}
                          </td>
                          <td>
                            {editingRow === index ? (
                              <input
                                type="text"
                                value={editedData.description || ''}
                                onChange={(e) => handleEditedDataChange('description', e.target.value)}
                                className="edit-input-v2"
                              />
                            ) : (
                              row.description
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="preview-controls-v2">
                  <div className="edit-controls-v2">
                    <button
                      type="button"
                      className="edit-selected-button-v2"
                      onClick={() => handleEditRow(0)}
                      disabled={editingRow !== null}
                    >
                      Edit First Row
                    </button>
                    {editingRow !== null && (
                      <div className="edit-save-cancel-v2">
                        <button
                          type="button"
                          className="save-edit-button-v2"
                          onClick={() => handleSaveRow(editingRow)}
                        >
                          Save Changes
                        </button>
                        <button
                          type="button"
                          className="cancel-edit-button-v2"
                          onClick={handleCancelEdit}
                        >
                          Cancel Edit
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="delete-controls-v2">
                    <button
                      type="button"
                      className="delete-selected-button-v2"
                      onClick={handleDeleteSelected}
                      disabled={selectedRows.size === 0}
                    >
                      Delete Selected ({selectedRows.size})
                    </button>
                  </div>
                </div>
                
                <div className="preview-actions-v2">
                  <button
                    type="button"
                    className="cancel-button-v2"
                    onClick={() => {
                      setShowPreview(false);
                      setExcelData([]);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="submit-button-v2"
                    onClick={handleBulkCreate}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : `Create ${excelData.length} Companies`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddCompanyModal;
